import { getProductStockForSite } from './logisticsPlanning';
import { getLocalYMD, resolveOrderLogisticsDate } from './storeLogistics';
import { sanitizeProductDisplayName } from './productLabels';
import { isOperationalInventorySaleItem } from './saleItems';

export const PRODUCTION_REQUEST_SOURCE_TYPES = {
  LOGISTICS: 'logistics',
  MANUAL: 'manual',
};

export const CLOSED_LOGISTICS_ORDER_STATUSES = new Set(['cancelado', 'entregado', 'retirado']);

export const PRODUCTION_WORKFLOW_STEP_TYPES = {
  PRODUCTION: 'produccion',
  PACKAGING: 'empaquetado',
  CUSTOM: 'custom',
};

const normalizeText = (value) => String(value || '').trim();
const normalizeComparableDate = (value) => String(value || '').trim();
const normalizeUuidArray = (value) =>
  (Array.isArray(value) ? value : [])
    .map((item) => String(item || '').trim())
    .filter(Boolean);

const getLinkedRequestLineIds = (row) => {
  if (!row || typeof row !== 'object') return [];

  const directIds = normalizeUuidArray(row.request_line_ids || row.production_request_line_ids);
  if (directIds.length > 0) return directIds;

  const metadataIds = normalizeUuidArray(
    row.metadata?.request_line_ids || row.metadata?.production_request_line_ids,
  );
  if (metadataIds.length > 0) return metadataIds;

  return [];
};

/**
 * FIX IMPORTANTE #13: matching robusto entre un entry de producción/step
 * y las líneas de pedido de una fecha.
 *
 * Reglas de prioridad:
 *  1) Si el entry trae `request_line_ids` explícitos → match por intersección.
 *     Es la forma CORRECTA y siempre debería usarse en payloads nuevos.
 *  2) Si el entry NO trae linkedIds:
 *     a) Y todas las líneas son de carga MANUAL sin site_id (lineSiteIds vacío),
 *        antes devolvíamos `true` siempre — peligroso porque mezcla entries de
 *        productos distintos del mismo key. Ahora exigimos que coincida la fecha
 *        operacional Y el producto (matching estricto cuando no hay vínculo).
 *     b) Si las líneas tienen site_id, comparamos también por site_id como antes.
 *
 * El caller debería guiar al RPC para SIEMPRE persistir request_line_ids; este
 * matching laxo solo cubre datos legacy sin esa info.
 */
const entryMatchesRequestLines = (entry, linesForDate) => {
  const lineIds = new Set(linesForDate.map((line) => String(line.id || '').trim()).filter(Boolean));
  const linkedIds = getLinkedRequestLineIds(entry);

  // Caso 1: hay linkedIds explícitos → match por intersección (forma correcta)
  if (linkedIds.length > 0) {
    return linkedIds.some((id) => lineIds.has(id));
  }

  // Caso 2 (legacy / carga manual): sin vínculo explícito.
  // Match estricto por producto y fecha operacional para no mezclar entries
  // de productos distintos que comparten el mismo aggregate key.
  const lineProductIds = new Set(
    linesForDate.map((line) => String(line.product_id || '').trim()).filter(Boolean),
  );
  const lineDates = new Set(
    linesForDate.map((line) => String(line.requested_date || '').trim()).filter(Boolean),
  );

  const candidateProductId = String(entry.product_id || entry.producto_id || '').trim();
  const candidateDate = String(entry.operational_date || entry.fecha || '').trim();

  // Si las líneas tienen product_id y el entry no matchea, descartar
  if (lineProductIds.size > 0 && candidateProductId && !lineProductIds.has(candidateProductId)) {
    return false;
  }

  // Si las líneas tienen fechas conocidas y el entry no matchea, descartar
  // (tomamos solo la parte de fecha, ya normalizada en el caller)
  if (lineDates.size > 0 && candidateDate) {
    const candidateDateOnly = candidateDate.split('T')[0];
    const matchesDate = Array.from(lineDates).some((d) => d.split('T')[0] === candidateDateOnly);
    if (!matchesDate) return false;
  }

  // Comparación opcional por sitio si las líneas lo tienen
  const lineSiteIds = new Set(linesForDate.map((line) => String(line.site_id || '').trim()).filter(Boolean));
  if (lineSiteIds.size === 0) {
    // Líneas sin site_id (carga manual): aceptamos solo si product/date matchean
    return true;
  }

  const candidateSiteId = String(
    entry.site_id || entry.sitio_id || entry.target_site_id || entry.source_site_id || '',
  ).trim();

  return candidateSiteId ? lineSiteIds.has(candidateSiteId) : false;
};

export const slugifyProductionValue = (value) =>
  normalizeText(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const buildProductionAggregateKey = (productId, productName) => {
  if (productId) return `product:${productId}`;
  const fallback = slugifyProductionValue(productName) || 'producto';
  return `name:${fallback}`;
};

export const createEmptyWorkflowStep = (index = 0) => ({
  key: `step-${Date.now()}-${index}`,
  name: index === 0 ? 'Produccion' : index === 1 ? 'Empaquetado' : '',
  type:
    index === 0
      ? PRODUCTION_WORKFLOW_STEP_TYPES.PRODUCTION
      : index === 1
        ? PRODUCTION_WORKFLOW_STEP_TYPES.PACKAGING
        : PRODUCTION_WORKFLOW_STEP_TYPES.CUSTOM,
  recipeProductId: '',
  recipeProductName: '',
  // FIX IMPORTANTE #12: sitio donde se ejecuta el paso (donde vive la materia prima
  // específica de este step). Vacío = usar el sitio global del registrador.
  // Útil para flujos multi-planta: producción en planta A, empaquetado en planta B.
  siteId: '',
  siteName: '',
});

const normalizeWorkflowStepType = (value) => {
  const normalized = normalizeText(value).toLowerCase();
  if (normalized === PRODUCTION_WORKFLOW_STEP_TYPES.PRODUCTION) return PRODUCTION_WORKFLOW_STEP_TYPES.PRODUCTION;
  if (normalized === PRODUCTION_WORKFLOW_STEP_TYPES.PACKAGING) return PRODUCTION_WORKFLOW_STEP_TYPES.PACKAGING;
  return PRODUCTION_WORKFLOW_STEP_TYPES.CUSTOM;
};

export const normalizeWorkflowSteps = (steps) =>
  (Array.isArray(steps) ? steps : [])
    .map((step, index) => {
      const name = normalizeText(step?.name) || `Paso ${index + 1}`;
      const key = normalizeText(step?.key) || `${slugifyProductionValue(name) || 'paso'}-${index + 1}`;

      return {
        key,
        name,
        type: normalizeWorkflowStepType(step?.type),
        recipeProductId: normalizeText(step?.recipeProductId || step?.recipe_product_id) || '',
        recipeProductName: normalizeText(step?.recipeProductName || step?.recipe_product_name) || '',
        // FIX IMPORTANTE #12: persistimos sitio del paso si fue configurado
        siteId: normalizeText(step?.siteId || step?.site_id) || '',
        siteName: normalizeText(step?.siteName || step?.site_name) || '',
      };
    })
    .filter((step, index, array) => step.key && array.findIndex((candidate) => candidate.key === step.key) === index);

export const findWorkflowStepByType = (steps, type) =>
  normalizeWorkflowSteps(steps).find((step) => step.type === type) || null;

export const buildProductionRequestLinesFromOrder = ({
  organizationId,
  order,
  orderItems,
  requestedDate,
}) =>
  (orderItems || [])
    .filter((item) => isOperationalInventorySaleItem(item))
    .map((item, index) => {
    const productName = sanitizeProductDisplayName(
      normalizeText(item.product_name_snapshot) || 'Producto',
    );
    const sourceFragment = item.product_id || slugifyProductionValue(productName) || `item-${index + 1}`;
    const targetDate = requestedDate || getLocalYMD(new Date());

    return {
      organization_id: organizationId,
      source_key: `logistics:${order.id}:${sourceFragment}:${index}`,
      source_type: PRODUCTION_REQUEST_SOURCE_TYPES.LOGISTICS,
      logistics_order_id: order.id,
      sale_id: order.sale_id || null,
      site_id: order.logistics_site_id || order.sale?.site_id || null,
      product_id: item.product_id || null,
      product_name: productName,
      quantity: Number(item.quantity || 0),
      requested_date: targetDate,
      notes: '',
      metadata: {
        customer_name: order.customer_snapshot?.name || order.customer_snapshot?.contact || 'Cliente web',
        order_created_at: order.created_at || null,
        order_status: order.status || 'recibido',
        logistics_date: resolveOrderLogisticsDate(order),
        requested_date: targetDate,
        source_index: index,
        tracking_code: order.tracking_code || order.id.slice(0, 8),
      },
      is_active: true,
    };
  });

export const buildProductionRequestLineTitle = (line) => {
  if (line.source_type === PRODUCTION_REQUEST_SOURCE_TYPES.LOGISTICS) {
    const tracking = line.metadata?.tracking_code || line.logistics_order_id?.slice(0, 8) || 'Pedido';
    return `#${tracking}`;
  }

  return 'Carga manual';
};

export const buildProductionRequestLineSubtitle = (line, siteName = '') => {
  const pieces = [];

  if (line.source_type === PRODUCTION_REQUEST_SOURCE_TYPES.LOGISTICS) {
    pieces.push(line.metadata?.customer_name || 'Cliente web');
  }

  if (siteName) pieces.push(siteName);
  if (line.notes) pieces.push(line.notes);

  return pieces.filter(Boolean).join(' - ');
};

export const isOperationalProductionRequestLine = (line) => {
  if (!line) return false;
  if (line.source_type !== PRODUCTION_REQUEST_SOURCE_TYPES.LOGISTICS) return true;
  return Boolean(line.product_id);
};

export const filterOperationalProductionRequestLines = (lines) =>
  (Array.isArray(lines) ? lines : []).filter((line) => isOperationalProductionRequestLine(line));

export const reconcileProductionRequestLinesWithOrders = async ({
  supabase,
  organizationId,
  lines,
  deactivateClosedLogistics = false,
}) => {
  const safeLines = Array.isArray(lines) ? lines.filter(Boolean) : [];
  const logisticsOrderIds = Array.from(
    new Set(
      safeLines
        .filter((line) => line.source_type === PRODUCTION_REQUEST_SOURCE_TYPES.LOGISTICS)
        .map((line) => line.logistics_order_id)
        .filter(Boolean),
    ),
  );

  if (logisticsOrderIds.length === 0) {
    return { data: safeLines, closedLineIds: [], skippedLineIds: [], error: null };
  }

  const ordersRes = await supabase
    .from('store_orders')
    .select('id, status')
    .eq('organization_id', organizationId)
    .in('id', logisticsOrderIds);

  if (ordersRes.error) {
    return { data: safeLines, closedLineIds: [], skippedLineIds: [], error: ordersRes.error };
  }

  const closedOrderIds = new Set(
    (ordersRes.data || [])
      .filter((order) => CLOSED_LOGISTICS_ORDER_STATUSES.has(normalizeText(order.status).toLowerCase()))
      .map((order) => order.id),
  );

  if (closedOrderIds.size === 0) {
    return { data: safeLines, closedLineIds: [], skippedLineIds: [], error: null };
  }

  const candidateClosedLines = safeLines
    .filter((line) => line.logistics_order_id && closedOrderIds.has(line.logistics_order_id))
    .filter((line) => line.id);

  // FIX CRÍTICO #7: antes de desactivar líneas con store_order cerrado,
  // verificamos si tienen producción asociada. Si la tienen, NO las desactivamos
  // para no perder la trazabilidad del lote producido respecto al pedido original.
  // Las líneas con producción se mantienen visibles para que el operador decida
  // manualmente qué hacer (cancelar producción, reasignar, etc.).
  let blockedLineIds = new Set();
  let skippedLineIds = [];

  if (deactivateClosedLogistics && candidateClosedLines.length > 0) {
    const dependenciesRes = await inspectProductionRequestLineDependencies({
      supabase,
      organizationId,
      lines: candidateClosedLines,
    });

    if (dependenciesRes.error) {
      // Si no podemos inspeccionar dependencias, ABORTAMOS la desactivación por seguridad
      // (mejor dejar líneas activas que perder trazabilidad).
      console.error(
        '[reconcileProductionRequestLinesWithOrders] No se pudieron inspeccionar dependencias; se aborta la desactivación.',
        dependenciesRes.error,
      );
      return {
        data: safeLines,
        closedLineIds: candidateClosedLines.map((line) => line.id),
        skippedLineIds: candidateClosedLines.map((line) => line.id),
        error: dependenciesRes.error,
      };
    }

    blockedLineIds = new Set(dependenciesRes.data?.blockedLineIds || []);
    skippedLineIds = candidateClosedLines
      .filter((line) => blockedLineIds.has(line.id))
      .map((line) => line.id);
  }

  // Solo desactivamos las líneas que NO tienen producción asociada
  const closedLineIds = candidateClosedLines
    .map((line) => line.id)
    .filter((id) => !blockedLineIds.has(id));

  let updateError = null;
  if (deactivateClosedLogistics && closedLineIds.length > 0) {
    const deactivateRes = await supabase
      .from('production_request_lines')
      .update({ is_active: false })
      .eq('organization_id', organizationId)
      .in('id', closedLineIds);

    updateError = deactivateRes.error || null;
  }

  // Filtramos para devolver solo las líneas que siguen activas (es decir,
  // las de pedidos abiertos + las de pedidos cerrados PERO con producción asociada).
  const stillActiveLines = safeLines.filter((line) => {
    if (!line.logistics_order_id) return true;
    if (!closedOrderIds.has(line.logistics_order_id)) return true;
    // Pedido cerrado: solo devolvemos si tiene producción (skipped/blocked)
    return blockedLineIds.has(line.id);
  });

  return {
    data: stillActiveLines,
    closedLineIds,
    skippedLineIds, // Líneas que no se desactivaron porque tienen producción asociada
    error: updateError,
  };
};

export const inspectProductionRequestLineDependencies = async ({
  supabase,
  organizationId,
  lines,
}) => {
  const safeLines = Array.isArray(lines) ? lines.filter(Boolean) : [];
  const lineIds = Array.from(new Set(safeLines.map((line) => line.id).filter(Boolean)));
  const lineById = new Map(safeLines.map((line) => [line.id, line]));
  const reasonsByLineId = new Map();

  lineIds.forEach((lineId) => {
    reasonsByLineId.set(lineId, {
      hasProduction: false,
      hasStepEntries: false,
    });
  });

  let productionRows = [];
  if (lineIds.length > 0) {
    const productionRes = await supabase
      .from('produccion')
      .select('id, production_request_line_ids')
      .eq('organization_id', organizationId)
      .eq('activo', true)
      .overlaps('production_request_line_ids', lineIds);

    if (productionRes.error) {
      return { data: null, error: productionRes.error };
    }

    productionRows = productionRes.data || [];
    productionRows.forEach((row) => {
      (Array.isArray(row.production_request_line_ids) ? row.production_request_line_ids : []).forEach((lineId) => {
        if (!reasonsByLineId.has(lineId)) return;
        reasonsByLineId.set(lineId, {
          ...reasonsByLineId.get(lineId),
          hasProduction: true,
        });
      });
    });
  }

  const productIds = Array.from(new Set(safeLines.map((line) => line.product_id).filter(Boolean)));
  const requestedDates = Array.from(new Set(safeLines.map((line) => normalizeComparableDate(line.requested_date)).filter(Boolean)));
  let stepEntries = [];

  if (productIds.length > 0 && requestedDates.length > 0) {
    const stepEntriesRes = await supabase
      .from('production_step_entries')
      .select('id, product_id, site_id, operational_date')
      .eq('organization_id', organizationId)
      .eq('is_active', true)
      .in('product_id', productIds)
      .in('operational_date', requestedDates);

    if (stepEntriesRes.error) {
      return { data: null, error: stepEntriesRes.error };
    }

    stepEntries = stepEntriesRes.data || [];
    stepEntries.forEach((entry) => {
      safeLines.forEach((line) => {
        if (!line.id || !line.product_id) return;
        if (line.product_id !== entry.product_id) return;
        if (normalizeComparableDate(line.requested_date) !== normalizeComparableDate(entry.operational_date)) return;
        if (line.site_id && entry.site_id && line.site_id !== entry.site_id) return;

        reasonsByLineId.set(line.id, {
          ...reasonsByLineId.get(line.id),
          hasStepEntries: true,
        });
      });
    });
  }

  const blockedLineIds = Array.from(reasonsByLineId.entries())
    .filter(([, reason]) => reason.hasProduction || reason.hasStepEntries)
    .map(([lineId]) => lineId);

  return {
    data: {
      blockedLineIds,
      reasonsByLineId,
      hasBlockedLines: blockedLineIds.length > 0,
      productionCount: productionRows.length,
      stepEntriesCount: stepEntries.length,
      linesById: lineById,
    },
    error: null,
  };
};

export const buildProductionRequestRows = ({
  requestLines,
  workflows,
  stepEntries,
  productions,
  products,
  selectedSiteId,
  stockByProductId,
}) => {
  const productMap = new Map((products || []).map((product) => [product.id, product]));
  const directStockMap = stockByProductId instanceof Map ? stockByProductId : new Map();
  const workflowMap = new Map(
    (workflows || []).map((workflow) => [
      workflow.product_id,
      {
        ...workflow,
        steps: normalizeWorkflowSteps(workflow.steps),
      },
    ]),
  );

  const requestLinesByKey = new Map();
  const productionTotalsByKeyDate = new Map();
  const productionItemsByKeyDate = new Map();
  const productionItemsByKey = new Map();
  const stepTotalsByKeyDateStep = new Map();
  const stepItemsByKeyDateStep = new Map();
  const stepItemsByKey = new Map();

  (requestLines || []).forEach((line) => {
    const key = buildProductionAggregateKey(line.product_id, line.product_name);
    const previous = requestLinesByKey.get(key) || {
      key,
      productId: line.product_id || null,
      name: line.product_name || 'Producto',
      lines: [],
      siteIds: new Set(),
      linesByDate: new Map(),
    };

    previous.lines.push(line);
    if (line.site_id) previous.siteIds.add(line.site_id);
    const dateKey = normalizeComparableDate(line.requested_date);
    const currentDateLines = previous.linesByDate.get(dateKey) || [];
    currentDateLines.push(line);
    previous.linesByDate.set(dateKey, currentDateLines);
    requestLinesByKey.set(key, previous);
  });

  (productions || []).forEach((row) => {
    const key = buildProductionAggregateKey(row.producto_id, row.producto_nombre);
    const dateKey = normalizeComparableDate(row.operational_date || row.fecha);
    const aggregateKey = `${key}::${dateKey}`;
    productionTotalsByKeyDate.set(aggregateKey, Number(productionTotalsByKeyDate.get(aggregateKey) || 0) + Number(row.cantidad || 0));

    const items = productionItemsByKey.get(key) || [];
    items.push(row);
    productionItemsByKey.set(key, items);

    const datedItems = productionItemsByKeyDate.get(aggregateKey) || [];
    datedItems.push(row);
    productionItemsByKeyDate.set(aggregateKey, datedItems);
  });

  (stepEntries || []).forEach((entry) => {
    const key = buildProductionAggregateKey(entry.product_id, entry.product_name || entry.metadata?.product_name);
    const dateKey = normalizeComparableDate(entry.operational_date);
    const aggregateKey = `${key}::${dateKey}::${entry.step_key}`;
    stepTotalsByKeyDateStep.set(aggregateKey, Number(stepTotalsByKeyDateStep.get(aggregateKey) || 0) + Number(entry.quantity || 0));

    const items = stepItemsByKeyDateStep.get(aggregateKey) || [];
    items.push(entry);
    stepItemsByKeyDateStep.set(aggregateKey, items);

    const anyDateItems = stepItemsByKey.get(`${key}::${entry.step_key}`) || [];
    anyDateItems.push(entry);
    stepItemsByKey.set(`${key}::${entry.step_key}`, anyDateItems);
  });

  return Array.from(requestLinesByKey.values())
    .map((group) => {
      const workflow = group.productId ? workflowMap.get(group.productId) || null : null;
      const steps = (workflow?.steps || []).map((step, index, array) => ({
        ...step,
        isFinalStep: index === array.length - 1,
        position: index + 1,
      }));
      const product = group.productId ? productMap.get(group.productId) || null : null;
      const stock = group.productId && directStockMap.has(group.productId)
        ? Number(directStockMap.get(group.productId) || 0)
        : getProductStockForSite(product, selectedSiteId);
      const primarySiteId = group.siteIds.size === 1 ? Array.from(group.siteIds)[0] : '';
      const requestedDates = Array.from(
        new Set(group.lines.map((line) => String(line.requested_date || '').trim()).filter(Boolean)),
      ).sort();

      const byDate = requestedDates.reduce((acc, requestedDate) => {
        const dateKey = normalizeComparableDate(requestedDate);
        const linesForDate = group.linesByDate.get(dateKey) || [];
        const pedido = linesForDate.reduce((total, line) => total + Number(line.quantity || 0), 0);
        const productionItemsForDate = (productionItemsByKeyDate.get(`${group.key}::${dateKey}`) || []).filter((row) =>
          entryMatchesRequestLines(row, linesForDate),
        );
        const finalCompleted = productionItemsForDate.reduce(
          (total, productionRow) => total + Number(productionRow.cantidad || 0),
          0,
        );
        const productionTarget = Math.max(pedido, 0);
        const pendingProduction = Math.max(productionTarget - finalCompleted, 0);

        acc[dateKey] = {
          requestedDate: dateKey,
          pedido,
          productionTarget,
          finalCompleted,
          pendingProduction,
          lines: linesForDate,
          productionItems: productionItemsForDate,
          steps: steps.map((step) => {
            const stepEntriesForDate = (stepItemsByKeyDateStep.get(`${group.key}::${dateKey}::${step.key}`) || []).filter((entry) =>
              entryMatchesRequestLines(entry, linesForDate),
            );
            const completed = step.isFinalStep
              ? finalCompleted
              : stepEntriesForDate.reduce((total, entry) => total + Number(entry.quantity || 0), 0);

            return {
              ...step,
              completed,
              target: productionTarget,
              isComplete: productionTarget <= 0 ? true : completed >= productionTarget,
              entries: stepEntriesForDate,
            };
          }),
        };

        return acc;
      }, {});

      return {
        key: group.key,
        productId: group.productId,
        name: group.name,
        brand: product?.brand || null,
        stock,
        workflow,
        steps,
        lines: group.lines,
        requestedDates,
        primarySiteId,
        productionItems: productionItemsByKey.get(group.key) || [],
        byDate,
        getMetricsForDate: (requestedDate) => {
          const normalizedDate = normalizeComparableDate(requestedDate);
          return byDate[normalizedDate] || byDate[requestedDates[0]] || {
            requestedDate: normalizedDate,
            pedido: 0,
            productionTarget: 0,
            finalCompleted: 0,
            pendingProduction: 0,
            lines: [],
            productionItems: [],
            steps: steps.map((step) => ({
              ...step,
              completed: 0,
              target: 0,
              isComplete: true,
              entries: [],
            })),
          };
        },
        getStepEntries: (stepKey, requestedDate = '') => {
          const normalizedDate = normalizeComparableDate(requestedDate);
          if (normalizedDate) {
            return stepItemsByKeyDateStep.get(`${group.key}::${normalizedDate}::${stepKey}`) || [];
          }
          return stepItemsByKey.get(`${group.key}::${stepKey}`) || [];
        },
      };
    })
    .sort((left, right) => {
      const leftFirstDate = left.requestedDates[0] || '';
      const rightFirstDate = right.requestedDates[0] || '';
      const leftPending = Number(left.getMetricsForDate(leftFirstDate).pendingProduction || 0);
      const rightPending = Number(right.getMetricsForDate(rightFirstDate).pendingProduction || 0);
      const leftPedido = Number(left.getMetricsForDate(leftFirstDate).pedido || 0);
      const rightPedido = Number(right.getMetricsForDate(rightFirstDate).pedido || 0);

      if (rightPending !== leftPending) return rightPending - leftPending;
      if (rightPedido !== leftPedido) return rightPedido - leftPedido;
      return left.name.localeCompare(right.name, 'es', { sensitivity: 'base' });
    });
};

export const buildProductionDateRange = (selectedDate) => {
  const normalizedDate = normalizeText(selectedDate) || getLocalYMD(new Date());
  const [year, month, day] = normalizedDate.split('-').map(Number);

  return {
    startISO: new Date(year, month - 1, day, 0, 0, 0, 0).toISOString(),
    endISO: new Date(year, month - 1, day, 23, 59, 59, 999).toISOString(),
  };
};
